import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getNotifications,
  markNotificationAsRead as markNotificationAsReadApi,
  markAllNotificationsAsRead as markAllNotificationsAsReadApi,
} from "@/lib/api/handlers/NotificationsHandling";
import { UserLog } from "@/types/UserLog";
import { ServerError } from "@/lib/api/errors/ErrorHandler";

export type NotificationStateType = {
  numUnread: number;
  notifications: UserLog[];
  notificationsUnread: UserLog[];
  hasMore: boolean;
  hasMoreUnread: boolean;
  view: "all" | "unread";
  error: string;
  loading: boolean;
  loadingAction: boolean;
};

/**
 * A fresh page 0 from the app-bar poll, merged into what the bell holds
 * (audit r1 A5-3). Nothing paged in yet ⇒ page 0 IS the list (a row gone on
 * the server goes here too). Rows paged in later ⇒ fresh page 0 first, then
 * every row held before that it does not carry — dropping the old page 0's
 * tail would leave a gap before the paged rows.
 */
function mergeSeed(fresh: UserLog[], held: UserLog[], pagedIn: boolean): UserLog[] {
  const page0 = fresh ?? [];
  if (!pagedIn) return page0;
  const ids = new Set(page0.map((n) => n.logId));
  return [...page0, ...held.filter((n) => !ids.has(n.logId))];
}

/** A further page appended without the rows already held (a newer cut overlaps). */
function appendPage(held: UserLog[], more: UserLog[]): UserLog[] {
  const ids = new Set(held.map((n) => n.logId));
  return [...held, ...(more ?? []).filter((n) => !ids.has(n.logId))];
}

export const useNotifications = (
  numUnreadProp: number,
  notificationsProp: UserLog[],
  notificationsUnreadProp: UserLog[],
  fetchDate: Date
) => {
  const queryClient = useQueryClient();
  const [notificationState, setNotificationState] =
    useState<NotificationStateType>({
      numUnread: numUnreadProp,
      notifications: notificationsProp,
      notificationsUnread: notificationsUnreadProp,
      hasMore: notificationsProp?.length === 10,
      hasMoreUnread: notificationsUnreadProp?.length === 10,
      view: "all",
      error: "",
      loading: false,
      loadingAction: false,
    });

  const pageRef = useRef(0);
  const pageRefUnread = useRef(0);
  // The cut (`olderThan`) of the newest seed: paging goes on from it. The
  // callbacks below are memoised on other values, so they read it here.
  const fetchDateRef = useRef(fetchDate);
  useEffect(() => {
    fetchDateRef.current = fetchDate;
  }, [fetchDate]);

  // Audit r1 A5-3: the app-bar poll hands a new seed every minute — take it.
  // Before, `useState` kept the mount's seed for good, so neither a new
  // request row nor the poll's unread count ever reached the bell. The same
  // seed again (react-query keeps unchanged arrays by reference) changes
  // nothing, so a local mark-as-read is not undone by a no-op poll.
  const seedRef = useRef({ numUnreadProp, notificationsProp, notificationsUnreadProp });
  useEffect(() => {
    const seed = seedRef.current;
    if (
      seed.numUnreadProp === numUnreadProp &&
      seed.notificationsProp === notificationsProp &&
      seed.notificationsUnreadProp === notificationsUnreadProp
    ) {
      return;
    }
    seedRef.current = { numUnreadProp, notificationsProp, notificationsUnreadProp };
    const pagedAll = pageRef.current > 0;
    const pagedUnread = pageRefUnread.current > 0;
    setNotificationState((prevState) => ({
      ...prevState,
      numUnread: numUnreadProp,
      notifications: mergeSeed(notificationsProp, prevState.notifications, pagedAll),
      notificationsUnread: mergeSeed(notificationsUnreadProp, prevState.notificationsUnread, pagedUnread),
      hasMore: pagedAll ? prevState.hasMore : notificationsProp?.length === 10,
      hasMoreUnread: pagedUnread ? prevState.hasMoreUnread : notificationsUnreadProp?.length === 10,
    }));
  }, [numUnreadProp, notificationsProp, notificationsUnreadProp]);

  const fetchAdditionalNotifications = useCallback(async () => {
    if (!notificationState.hasMore) return [];

    setNotificationState((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      let newLogs = await getNotifications(++pageRef.current, 10, fetchDateRef.current);

      // Was `new Error(...)` — constructed and never thrown, so a ServerError
      // fell straight through and got cast to UserLog[] two lines down.
      if ("isServerError" in newLogs && newLogs.isServerError === true)
        throw new Error(newLogs.message);

      newLogs = newLogs as UserLog[];

      setNotificationState((prevState) => ({
        ...prevState,
        notifications: appendPage(prevState.notifications, newLogs),
        hasMore: newLogs?.length === 10,
        loading: false,
      }));
    } catch (error) {
      let errorMessage = "unknown";
      if (error instanceof Error) errorMessage = error.message;

      setNotificationState((prevState) => ({
        ...prevState,
        error: errorMessage,
        loading: false,
      }));
    }
  }, [notificationState.hasMore]);

  const fetchAdditionalUnreadNotifications = useCallback(async () => {
    if (!notificationState.hasMoreUnread) return [];

    setNotificationState((prevState) => ({
      ...prevState,
      loading: true,
    }));
    try {
      let newLogs = await getNotifications(
        ++pageRefUnread.current,
        10,
        fetchDateRef.current,
        false
      );

      // Was `new Error(...)` — constructed and never thrown, so a ServerError
      // fell straight through and got cast to UserLog[] two lines down.
      if ("isServerError" in newLogs && newLogs.isServerError === true)
        throw new Error(newLogs.message);

      newLogs = newLogs as UserLog[];

      setNotificationState((prevState) => ({
        ...prevState,
        notificationsUnread: appendPage(prevState.notificationsUnread, newLogs),
        hasMoreUnread: newLogs?.length === 10,
        loading: false,
      }));
    } catch (error) {
      let errorMessage = "unknown";
      if (error instanceof Error) errorMessage = error.message;

      setNotificationState((prevState) => ({
        ...prevState,
        error: errorMessage,
        loading: false,
      }));
    }
  }, [notificationState.hasMoreUnread]);

  const markNotificationAsRead = useCallback(async (id: string) => {
    setNotificationState((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      await markNotificationAsReadApi(id);
      // The appbar seed query caches the unread rows/count and every shell
      // switch remounts SAppBar — without this, marked-read notifications
      // resurrect on the badge for the staleTime window.
      void queryClient.invalidateQueries({ queryKey: ["initialiseUserBase"] });

      setNotificationState((prevState) => {
        const updatedNotifications = prevState.notifications.map(
          (notification) =>
            notification.logId === id
              ? { ...notification, read: true }
              : notification
        );

        const initialUnreadLength = prevState.notificationsUnread?.length;
        const updatedUnread = prevState.notificationsUnread.filter(
          (notification) => notification.logId !== id
        );

        return {
          ...prevState,
          notifications: updatedNotifications,
          notificationsUnread: updatedUnread,
          // From the CURRENT count: this callback is memoised, and the
          // poll re-seeds the count while the bell is open.
          numUnread:
            prevState.numUnread -
            (initialUnreadLength - updatedUnread?.length),
          loading: false,
        };
      });
    } catch (error) {
      let errorMessage = "unknown";
      if (error instanceof Error) errorMessage = error.message;

      setNotificationState((prevState) => ({
        ...prevState,
        error: errorMessage,
        loading: false,
      }));
    }
  }, [queryClient]);

  const markAllNotificationsAsRead = useCallback(async () => {
    setNotificationState((prevState) => ({
      ...prevState,
      loading: true,
    }));
    try {
      await markAllNotificationsAsReadApi();
      void queryClient.invalidateQueries({ queryKey: ["initialiseUserBase"] });

      setNotificationState((prevState) => {
        const updatedNotifications = prevState.notifications.map(
          (notification) =>
            notification.read === false
              ? { ...notification, read: true }
              : notification
        );

        return {
          ...prevState,
          notifications: updatedNotifications,
          notificationsUnread: [],
          numUnread: 0,
          loading: false,
        };
      });
    } catch (error) {
      let errorMessage = "unknown";
      if (error instanceof Error) errorMessage = error.message;

      setNotificationState((prevState) => ({
        ...prevState,
        error: errorMessage,
        loading: false,
      }));
    }
  }, [queryClient]);

  const toggleNotificationView = useCallback(() => {
    setNotificationState((prevState) => ({
      ...prevState,
      view: prevState.view === "all" ? "unread" : "all",
    }));
  }, []);

  async function handleNotificationUpdate(
    id: string,
    actionMethod: (
      id: string
    ) => Promise<void | ServerError> | Promise<string | ServerError>,
    notification: UserLog
  ) {
    if (id === "000000000000000000000000") return;
    setNotificationState((prevState) => ({
      ...prevState,
      loadingAction: true,
    }));

    const initialLengthUnread = notificationState.notificationsUnread?.length;
    const initialLength = notificationState.notifications?.length;

    let unreadTemp = notificationState.notificationsUnread.filter(
      (n) => n.logId !== notification.logId
    );
    let normalTemp = notificationState.notifications.filter(
      (n) => n.logId !== notification.logId
    );

    try {
      // Execute the dynamic method (e.g., acceptEventRole, changeCompany)
      await actionMethod(id);
      // Audit r5 E4: the button's click bubbles to the row's mark-as-read,
      // whose seed refetch can read page 0 and the count from BEFORE this
      // write — and the hook takes every seed (A5-3), so the declined row came
      // back with live buttons until the next poll. Invalidating here cancels
      // that refetch and reads the seed after the write; the badge follows the
      // server (PB-NCA-4).
      void queryClient.invalidateQueries({ queryKey: ["initialiseUserBase"] });

      // Fetch the latest log if a new one has appeared in normal notifications
      const newLog: UserLog[] =
        initialLength !== normalTemp?.length
          ? ((await getNotifications(0, 1, new Date())) as UserLog[])
          : [];

      // Fetch the latest log if a new one has appeared in undecided notifications
      const newUnreadNotification: UserLog[] =
        notificationState.hasMoreUnread &&
        initialLengthUnread !== unreadTemp?.length
          ? ((await getNotifications(
              initialLengthUnread - 1,
              1,
              fetchDateRef.current,
              false
            )) as UserLog[])
          : [];

      // Ensure there is a valid new log before adding it at the top
      if (newLog?.length > 0) {
        normalTemp = [newLog[0], ...normalTemp];
      }

      // Ensure there is a valid new undecided log before adding it at the bottom
      if (newUnreadNotification?.length > 0) {
        unreadTemp = [...unreadTemp, newUnreadNotification[0]];
      }

      setNotificationState((prevState) => ({
        ...prevState,
        loadingAction: false,
        notifications: normalTemp,
        notificationsUnread: unreadTemp,
      }));
    } catch (error) {
      let errorMessage = "unknown";
      if (error instanceof Error) errorMessage = error.message;

      setNotificationState((prevState) => ({
        ...prevState,
        error: errorMessage,
        loadingAction: false,
      }));
    }
  }

  return {
    notificationState,
    setNotificationState,
    fetchAdditionalNotifications,
    fetchAdditionalUnreadNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    toggleNotificationView,
    handleNotificationUpdate,
  };
};
